import { useContext, useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Auth } from "aws-amplify";
import AuthContext from "../../contexts/AuthContext";
import db from "../../common/db";
import Sheet from "react-modal-sheet";
import "./ProfileModal.css";

const ProfileModal = ({ isOpen, onClose }) => {
  const Context = useContext(AuthContext);
  const [imageError, setImageError] = useState(false);
  // Use dropdown for tablet and desktop (768px+), bottom sheet only for mobile
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset image error state when modal opens
  useEffect(() => {
    if (isOpen) {
      setImageError(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const user = Context?.state?.user?.attributes || {};
  const userName = user.name || user.email?.split("@")[0] || "User";
  const userEmail = user.email || "";
  const userPicture = user.picture;

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "U";
    const words = name.trim().split(" ").filter(Boolean);
    if (words.length === 0) return "U";
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const initials = getInitials(userName);
  const showImage = userPicture && !imageError;

  const handleImageError = () => {
    setImageError(true);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      window.localStorage.clear();
      db.delete();
      Context.setValue({
        user: { attributes: {} },
        signedIn: false,
        refreshToken: null,
        domain: null,
      });
    } catch (err) {
      console.log("Logout err - ", err);
    }
  };

  // Shared content component
  const ModalContent = ({ isDesktopView = false }) => (
    <div className={`profile-modal ${isDesktopView ? 'profile-modal--desktop' : ''}`}>
      {/* User Info */}
      <div className="profile-modal__user">
        <div className="profile-modal__avatar">
          {showImage ? (
            <img
              src={userPicture}
              alt={userName}
              onError={handleImageError}
            />
          ) : (
            <span className="profile-modal__initials">{initials}</span>
          )}
        </div>
        <div className="profile-modal__info">
          <h3 className="profile-modal__name">{userName}</h3>
          <p className="profile-modal__email">{userEmail}</p>
        </div>
      </div>

      {/* Divider */}
      <div className="profile-modal__divider" />

      {/* Logout Button */}
      <button className="profile-modal__logout-btn" onClick={handleLogout}>
        <Icon icon="heroicons:arrow-right-on-rectangle" width="20" height="20" />
        Logout
      </button>
    </div>
  );

  // Desktop: Simple dropdown
  if (isDesktop) {
    return (
      <>
        <div className="profile-modal-backdrop" onClick={onClose} />
        <div className="profile-modal-dropdown">
          <ModalContent isDesktopView={true} />
        </div>
      </>
    );
  }

  // Mobile: Bottom sheet
  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content-height">
      <Sheet.Container className="profile-modal-container">
        {/* Floating Close Button */}
        <button
          className="profile-modal__close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon icon="iconamoon:close" width="24" height="24" color="currentColor" style={{ display: 'block' }} />
        </button>

        <Sheet.Header className="profile-modal-sheet-header">
          <div className="profile-modal__handle" />
        </Sheet.Header>

        <Sheet.Content>
          <ModalContent />
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onClick={handleBackdropClick} />
    </Sheet>
  );
};

export default ProfileModal;
